import { apiRequest, apiRequestWithEtag, type IEtagged } from '@/domains/api';

/**
 * survey-api's shared column layouts of the Work pages (SR-500): one per page and survey, read by
 * every member of staff, saved and reset under `work_pages.columns` (Data team, Administrator) with
 * the revision quoted — a first save quotes 0 — 409 `revision-conflict` on a stale one.
 */

const COLUMNS = '/api/v1/admin/work-page-columns';

/** The Work pages that keep a shared layout. */
export type WorkPage = 'dd_calls';

/** One survey's layout: the columns shown in order — a page column's id or a field code — and
 *  the page's own ids when it was saved; who saved it last (null once the account is gone). */
export interface IWorkPageLayout {
  survey: string;
  order: string[];
  known: string[];
  revision: number;
  updatedAt: string;
  updatedBy: Nullable<{ accountId: string; email: Nullable<string> }>;
}

export const workPagesApi = {
  async layouts(page: WorkPage, signal?: AbortSignal): Promise<IWorkPageLayout[]> {
    const answer = await apiRequest<{ layouts?: IWorkPageLayout[] }>(`${COLUMNS}/${page}`, { signal });
    return answer.layouts ?? [];
  },

  save(
    page: WorkPage,
    survey: string,
    revision: number,
    body: { order: string[]; known: string[] }
  ): Promise<IEtagged<IWorkPageLayout>> {
    return apiRequestWithEtag<IWorkPageLayout>(`${COLUMNS}/${page}/${encodeURIComponent(survey)}`, {
      method: 'PUT',
      body,
      headers: { 'If-Match': `"${revision}"` }
    });
  },

  async reset(page: WorkPage, survey: string, revision: number): Promise<void> {
    await apiRequest<null>(`${COLUMNS}/${page}/${encodeURIComponent(survey)}`, {
      method: 'DELETE',
      headers: { 'If-Match': `"${revision}"` }
    });
  }
};
